import {
  storedStyleOf,
  studioOf,
  type PlatformSettings,
  type StyleChoice,
  type StyleModel,
} from '@base/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { Button, ErrorBox, Loading, PageHeader } from '../../components/ui.tsx';
import {
  DEFAULT_LOGO_URL,
  FACTORY_STYLE,
  PLATFORM_KEY,
  usePlatformSettings,
} from '../../platform.tsx';
import { SISTEMA_NOME } from '../../sistema.ts';
import { initialStudio, sameStudio } from '../../studio/state.ts';
import type { StudioBrand } from '../../studio/ColorParts.tsx';
import { Studio } from '../../studio/Studio.tsx';

/**
 * Configuração › Estilo (ADR 0128, fase 5): o Estúdio de estilos dentro do sistema. Fica guardada
 * a escolha (layout, paleta e acabamento pelo nome, com os ajustes), nunca o tema calculado:
 * quando a base melhora uma parte, o sistema acompanha. Montar do zero é no Guia visual.
 */
export function StylePage() {
  const settings = usePlatformSettings();
  if (!settings.data) return <Loading />;
  return <StyleEditor initial={settings.data} />;
}

function StyleEditor({ initial }: { initial: PlatformSettings }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const readOnly = !can(me, 'platform.settings.manage');
  const current = initialStudio(initial.style, FACTORY_STYLE);
  const [value, setValue] = useState(current);
  const startBrand: StudioBrand = { name: initial.appName, logo: initial.logo };
  const [brand, setBrand] = useState(startBrand);
  const brandChanged = brand.name !== startBrand.name || brand.logo !== startBrand.logo;
  const save = useMutation({
    mutationFn: async (style: StyleChoice | null) => {
      // O nome e o logo são da Configuração › Plataforma: gravam lá, com o resto como está.
      if (brandChanged) {
        const { updatedAt: _u, theme: _t, style: _s, ...rest } = initial;
        await api.put<PlatformSettings>('/platform/settings', {
          ...rest,
          appName: brand.name,
          logo: brand.logo,
        });
      }
      return api.put<PlatformSettings>('/platform/style', { style });
    },
    onSuccess: (data) => qc.setQueryData(PLATFORM_KEY, data),
  });
  const saveModels = useMutation({
    mutationFn: (models: StyleModel[]) =>
      api.put<PlatformSettings>('/platform/style-models', { models }),
    onSuccess: (data) => qc.setQueryData(PLATFORM_KEY, data),
  });
  const saved = initial.style ? studioOf(initial.style) : null;
  const unchanged = !initial.theme && sameStudio(current, value) && !brandChanged;

  const navigate = useNavigate();
  const useButton = !readOnly && (
    <>
      {initial.style && (
        <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate(null)}>
          {t('style.backToFactory')}
        </Button>
      )}
      <Button
        disabled={save.isPending || unchanged}
        onClick={() => save.mutate(storedStyleOf(value))}
      >
        {save.isPending
          ? t('style.saving')
          : save.isSuccess && unchanged
            ? t('style.saved')
            : t('style.use')}
      </Button>
    </>
  );
  return (
    <>
      <div className="flex flex-col gap-4">
        <PageHeader
          title={t('style.title')}
          description={t('style.about', { sistema: SISTEMA_NOME })}
        />
        <ErrorBox error={save.error} />
        {initial.theme && (
          <p className="rounded-lg border border-rule bg-tonal p-3 text-sm" role="status">
            {t('style.customActive')}
          </p>
        )}
        {(initial.accentColor || initial.paperColor || initial.inkColor) && (
          <p className="text-sm text-muted">{t('style.platformColors')}</p>
        )}
        {readOnly && <p className="text-sm text-muted">{t('style.readOnly')}</p>}
        <Link
          to="/settings/design"
          className="text-sm text-brand-text underline-offset-2 hover:underline"
        >
          {t('style.fromScratch')}
        </Link>
      </div>
      <Studio
        value={value}
        onChange={setValue}
        current={initial.theme ? null : (saved ?? current)}
        appName={brand.name ?? SISTEMA_NOME}
        logoUrl={DEFAULT_LOGO_URL}
        brand={brand}
        onBrandChange={setBrand}
        models={initial.styleModels}
        onModelsChange={readOnly ? undefined : saveModels.mutateAsync}
        readOnly={readOnly}
        actions={useButton}
        onClose={() => void navigate({ to: '/settings' })}
      />
    </>
  );
}
